import React, { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";

import { NavLink } from "react-router-dom";
import Toolbar from "@mui/material/Toolbar";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import { useAppSelector, useAppDispatch } from "../../app/hooks";

import { selectAuth } from "../../features/auth/authSlice";
import { selectPreference } from "../../features/preferences/preferencesSlice";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { Avatar, Box, Divider, Tooltip, useTheme } from "@mui/material";
import {
  Refresh,
  Notifications as NotificationsIcon,
  MarkEmailRead,
} from "@mui/icons-material";
import { t } from "i18next";
import {
  markAsReadNotification,
  selectProcesses,
} from "../../features/process/processSlices";
import { useTranslation } from "react-i18next";
import { Role } from "../../features/auth/types/authType";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { RoleTypes } from "../../features/auth/types/roleTypes";
import { notificationLink } from "../../features/notifications/notificationAudiences";
import { Notification } from "../../features/process/types/processTypes";

function NavbarContent() {
  const { user } = useAppSelector(selectAuth);
  const { pageTitle } = useAppSelector(selectPreference);
  const dispatch = useAppDispatch();
  const { t: te } = useTranslation("enums");
  const {
    popupNotificationsWithCount: { notifications, totalCount },
  } = useAppSelector(selectProcesses);
  const theme = useTheme();

  const canView =
    isPrivilegedTransaction(
      (user?.roles ?? []) as Role[],
      RoleTypes.ViewNotifications,
    ) ||
    isPrivilegedTransaction(
      (user?.roles ?? []) as Role[],
      RoleTypes.ViewWall,
    );

  const [notifyAnchorEl, setNotifyAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const isNotifyMenuOpen = Boolean(notifyAnchorEl);

  const canMarkRead =
    isPrivilegedTransaction(
      (user?.roles ?? []) as Role[],
      RoleTypes.MarkAsReadNotifications,
    ) ||
    isPrivilegedTransaction(
      (user?.roles ?? []) as Role[],
      RoleTypes.ViewWall,
    );

  const handleNotifyMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifyAnchorEl(event.currentTarget);
  };

  const handleMarkAsRead = (
    event: React.MouseEvent,
    notificationId: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(markAsReadNotification(notificationId));
  };

  function refreshPage() {
    window.location.reload();
  }

  const handleNotifyMenuClose = () => {
    setNotifyAnchorEl(null);
  };

  const linkFor = (row: Notification) => notificationLink(row);

  const formatWhen = (createdAt?: Date | string) => {
    if (!createdAt) return "";
    return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
  };

  const [header, setHeader] = useState("");
  useEffect(() => {
    if (user && user.company) {
      let headerTitle = user.company?.displayName as string;
      if (theme.direction === "rtl")
        headerTitle = user.company.nameAmharic as string;
      setHeader(headerTitle);
    } else {
      setHeader(pageTitle);
    }
  }, [user, pageTitle, theme.direction]);

  const notifyMenuId = "primary-notifications-menu";

  const renderNotifyMenu = (
    <Menu
      anchorEl={notifyAnchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={notifyMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isNotifyMenuOpen}
      onClose={handleNotifyMenuClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "hidden",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1.5,
          width: 300,
          maxHeight: 360,
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.default",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {t("notifications")}
        </Typography>
        {(totalCount ?? 0) > 0 && (
          <Typography variant="caption" color="text.secondary">
            {totalCount} {t("new").toLowerCase()}
          </Typography>
        )}
      </Box>
      <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
        {notifications.length === 0 ? (
          <MenuItem disabled sx={{ py: 2, justifyContent: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("noNotifications")}
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((row) => (
            <MenuItem
              key={row._id}
              onClick={handleNotifyMenuClose}
              component={NavLink}
              to={linkFor(row)}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                py: 1,
                px: 1.5,
                whiteSpace: "normal",
              }}
            >
              <Avatar
                alt=""
                src={
                  row.wallPost
                    ? undefined
                    : row.employee?.facePhoto ?? undefined
                }
                sx={{ width: 28, height: 28, mt: 0.25, fontSize: 12 }}
              >
                {row.wallPost ? "W" : row.employee?.givenName?.[0]}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.35,
                  }}
                >
                  {te(row.message)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatWhen(row.createdAt)}
                </Typography>
              </Box>
              {canMarkRead && (
                <Tooltip title={t("markAsRead")}>
                  <IconButton
                    size="small"
                    color="inherit"
                    sx={{ mt: -0.25, opacity: 0.6, "&:hover": { opacity: 1 } }}
                    onClick={(e) => handleMarkAsRead(e, row._id as string)}
                  >
                    <MarkEmailRead fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          ))
        )}
      </Box>
      {(totalCount ?? 0) > 0 && [
        <Divider key="notify-divider" />,
        <MenuItem
          key="notify-view-all"
          component={NavLink}
          to="/app/notifications"
          onClick={handleNotifyMenuClose}
          sx={{ py: 1, justifyContent: "center" }}
        >
          <Typography variant="body2" color="primary" fontWeight={500}>
            {t("viewAll")}
          </Typography>
        </MenuItem>,
      ]}
    </Menu>
  );

  return (
    <>
      <Typography
        data-aos="fade-left"
        sx={{
          fontWeight: 900,
          fontFamily: "Open Sans",
          p: 0.5,
          display: { xs: "none", md: "flex" },
          textTransform: "capitalize",
        }}
        variant="h3"
        noWrap
      >
        {header}
      </Typography>
      <Box sx={{ flex: "1 1 auto" }} />
      <Toolbar disableGutters>
        {canView && (
          <Tooltip title={t("notifications")}>
            <IconButton
              aria-label="notifies"
              aria-controls={notifyMenuId}
              aria-haspopup="true"
              onClick={handleNotifyMenuOpen}
              color="inherit"
            >
              <Badge badgeContent={totalCount} color="secondary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t("refresh")}>
          <IconButton color="inherit" onClick={refreshPage}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Typography sx={{ display: { xs: "flex", md: "none" } }}>
          {user?.name}
        </Typography>
        <Typography sx={{ display: { xs: "none", md: "flex" } }}>
          {t("welcome") + " " + user?.name}
        </Typography>
      </Toolbar>
      {renderNotifyMenu}
    </>
  );
}

export default NavbarContent;
